import React, { useState, useEffect, useContext } from 'react';
import TitleBar from '../components/TitleBar.jsx';
import '../styles/release_notifications.css';
import { urlValue, loadingContext, unfolder, openAlert, useTranslate } from '../lib';
import axios from 'axios';
import { Alerts } from '../components/common/Alerts';
import DataTable from '../components/provider/datatable/Datatable';

const ReleaseNotifications = () => {
  const [loading, setLoading] = useContext(loadingContext);
  const [alert, setAlert] = useState({ title: '', visible: false });
  const [notifications, setNotifications] = useState([]);
  const [formData, setFormData] = useState({ title: '', description: '', date: '' });
  const [editId, setEditId] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const { t } = useTranslate();
  const getNotifications = async () => {
    try {
      setLoading(true);
      const id = unfolder(localStorage.getItem('hashInfo'), 'IANestingKey').id;
      const token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').token;
      const res = await axios.get(`${urlValue}/studio/release-notification`, {
        headers: { user: id, 'access-token': token }
      });
      setNotifications(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getNotifications();
  }, [refresh]);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const id = unfolder(localStorage.getItem('hashInfo'), 'IANestingKey').id;
    const token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').token;
    const csrfToken = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').csrfToken
    setLoading(true);
    try {
      if (editId) {
        await axios.put(`${urlValue}/studio/release-notification/${editId}`, formData, {
          headers: { 
            user: id, 
            'access-token': token,
            'X-CSRF-Token': csrfToken
          },
          withCredentials: true,
        });
      } else {
        await axios.post(`${urlValue}/studio/release-notification`, formData, {
          headers: { 
            user: id, 
            'access-token': token,
            'X-CSRF-Token': csrfToken
          },
          withCredentials: true,
        });
      }
      setFormData({ title: '', description: '', date: '' });
      setEditId(null);
      setRefresh(!refresh);
      openAlert('#alert-ac', () => setAlert({ title: t('alerts.notificationSaved'), visible: true }), 3000, () => setAlert({ visible: false }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (notification) => {
    setFormData({
      title: notification.title,
      description: notification.description,
      date: notification.date?.slice(0, 10) || ''
    });
    setEditId(notification.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    const token = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').token;
    const csrfToken = unfolder(localStorage.getItem('hashUser'), 'IANestingKey').csrfToken
    setLoading(true);
    try {
      await axios.delete(`${urlValue}/studio/release-notification/${id}`, {
        headers: {
          'access-token': token,
          'X-CSRF-Token': csrfToken
        },
        withCredentials: true,
      });
      setRefresh(!refresh);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { header: t('common.title'), field: 'title', width: '30%' },
    { header: t('common.description'), field: 'description', width: '50%' },
    { header: t('common.date'), field: 'date', width: '10%' },
    {
      header: t('common.actions'),
      field: 'actions',
      width: '10%',
      align: 'center',
      render: (_, item) => (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
          <img src="/icons/edit.png" alt={t('common.edit')} style={{ width: 15, cursor: 'pointer' }} onClick={() => handleEdit(item)} />
          <img src="/icons/trash.png" alt={t('common.delete')} style={{ width: 15, cursor: 'pointer' }} onClick={() => handleDelete(item.id)} />
        </div>
      )
    }
  ];

  return (
    <div className='general-container'>
      <TitleBar />
      <div className='primary-container d-flex flex-row flex-wrap justify-content-between mt-3'>
        <h1>{t('menu.releaseNotifications')}</h1>
        <div className="box-tool">
          <h2>{editId ? t('menu.editNotification') : t('menu.newNotification')}</h2>
          <form onSubmit={handleSubmit} className='d-flex flex-row flex-wrap'>
            <div className='items-little'>
              <div className='input-container'>
                <label>{t('common.title')}:</label>
                <textarea name="title" value={formData.title} onChange={handleChange} required rows="2" />
              </div>
            </div>
            <div className='items-little'>
              <div className='input-container'>
                <label>{t('common.description')}:</label>
                <textarea name="description" value={formData.description} onChange={handleChange} required rows="2" />
              </div>
            </div>
            <div className='items-little'>
              <div className='input-container'>
                <label>{t('common.date')}:</label>
                <input type="date" name="date" value={formData.date} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '15px', border: '1px solid #cccccc' }} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
              <button type="submit" style={{ padding: '8px 20px', borderRadius: 5, backgroundColor: '#0038A8', color: 'white', border: 'none' }}>
                {editId ? 'Update notification' : t('menu.createNotification')}
              </button>
              {editId && (
                <button type="button" onClick={() => { setEditId(null); setFormData({ title: '', description: '', date: '' }) }} style={{ marginLeft: 10, padding: '8px 20px', borderRadius: 5, backgroundColor: '#999', color: 'white', border: 'none' }}>
                  {t('common.cancel')}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
      <div className="primary-container mt-3">
        <DataTable
          columns={columns}
          data={notifications}
          keyField="id"
          emptyMessage={t('menu.noNotificationsFound')}
        />
      </div>
      {alert.visible && <Alerts id="alert-ac" title={alert.title} />}
    </div>
  );
};

export default ReleaseNotifications;